import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocale } from '../../i18n/locale-context';
import { formatBudgetCompact } from '../../lib/format';
import { centreInScroller } from '../../lib/use-visual-viewport-sheet-height';
import { countryConfig } from '../../country';

/**
 * A so'm amount typed as digits and SHOWN grouped — «100 000 000», not
 * «100000000» — with a live «= 100 mln so'm» under it and optional quick picks.
 *
 * Igor's phone run, 17.10 (Redmi, uz, «Qidiruvni sozlash»): the floor read
 * «100000000», nine zeros nobody can count at a glance, and the field sat
 * half under the keyboard. Two saved searches on prod hold a floor of 5 and two
 * of 500 so'm (17.10, read-only) — almost certainly «5 mln» and «500 mln» with
 * the zeros lost. The grouping and the caption are what make that visible
 * while typing.
 *
 * ## Contract
 *
 * `value` / `onChange` carry DIGITS ONLY (`''` = no bound), exactly what the
 * plain `<input type="number">` this replaces held, so every caller keeps
 * sending `Number(value)` and the stored value stays a plain integer. Grouping
 * is display only.
 *
 * `type="text"` + `inputMode="numeric"`: a number input cannot show spaces, and
 * the numeric keypad is what the phone needs anyway. Anything that is not a
 * digit is dropped on input, so a paste of «1 000 000 000», «1,000,000,000» or
 * «1 000 000 000 so'm» lands as 1000000000.
 *
 * Plain arithmetic, never `Intl` — Chrome ships no `uz` number data (see
 * `formatBudgetCompact` in lib/format.ts).
 */

/** 15 digits = 999 trillion so'm: far beyond any lot, and still an exact
 *  integer in a JS number (2^53 ≈ 9·10^15), which `Number(value)` relies on. */
const MAX_DIGITS = 15;

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Digits → «1 000 000». Same regular-space grouping as `formatBudget`. */
export function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** Whatever was typed or pasted → canonical digits: no separators, no leading
 *  zeros (a lone «0» stays), at most {@link MAX_DIGITS}. */
export function budgetDigits(raw: string): string {
  return raw
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '')
    .slice(0, MAX_DIGITS);
}

/** Index in `formatted` just after its `n`-th digit — where the caret goes. */
function caretAfterDigits(formatted: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) seen++;
    if (seen === n) return i + 1;
  }
  return formatted.length;
}

/**
 * Quick picks, whole so'm — from what real saved searches use (prod, 17.10,
 * read-only: 144 of 7 425 searches carry a floor, 96 a ceiling). Floors cluster
 * at 100 mln (19), 50 mln (18), 10 mln (16), then 500 mln and 1 mlrd (5 each);
 * ceilings at 10 mlrd (16), 500 mln (15), 100 mln (11), 1 mlrd (9).
 *
 * Uzbekistan only: these are so'm magnitudes, and a tenge budget is another
 * scale — a KZ build gets the grouped field and the caption without picks
 * rather than picks nobody chose.
 */
export const BUDGET_FLOOR_PICKS: readonly number[] =
  countryConfig.code === 'uz' ? [10e6, 50e6, 100e6, 500e6, 1e9] : [];
export const BUDGET_CEILING_PICKS: readonly number[] =
  countryConfig.code === 'uz' ? [100e6, 500e6, 1e9, 10e9] : [];

export interface BudgetInputProps {
  /** Digits only; `''` = no bound. */
  value: string;
  /** Receives digits only. */
  onChange: (digits: string) => void;
  /** Quick-pick amounts in whole so'm; none rendered when empty/omitted. */
  presets?: readonly number[];
  /** A chip was tapped — for a caller that applies on blur/Enter (the catalog
   *  filter), so a pick takes effect at once instead of waiting for a blur
   *  that never comes. `onChange` fires as well. */
  onPick?: (digits: string) => void;
  onBlur?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  id?: string;
  placeholder?: string;
  /** Classes for the `<input>` itself — the caller's field styling. */
  className?: string;
  /** Wrapper classes. */
  wrapperClassName?: string;
  /** The «= 100 mln so'm» line. Off only where a one-line desktop filter row
   *  has no room for a second line under the field. Default on. */
  showCaption?: boolean;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export function BudgetInput({
  value,
  onChange,
  presets,
  onPick,
  onBlur,
  onKeyDown,
  id,
  placeholder,
  className,
  wrapperClassName,
  showCaption = true,
  ...aria
}: BudgetInputProps) {
  const { t, locale } = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  // How many digits sat left of the caret at the last keystroke — restored
  // after the regrouped value renders, so typing in the middle of «1 000 000»
  // does not throw the caret to the end.
  const caretDigits = useRef<number | null>(null);

  const shown = groupDigits(value);

  useIsomorphicLayoutEffect(() => {
    const el = inputRef.current;
    const n = caretDigits.current;
    caretDigits.current = null;
    if (!el || n == null || document.activeElement !== el) return;
    const pos = caretAfterDigits(shown, n);
    el.setSelectionRange(pos, pos);
  }, [shown]);

  /*
   * Keep the field above the keyboard — the other half of 10a (Redmi, Chrome:
   * «Byudjet, so'mdan» cut in half by the keyboard). Chrome scrolls the CARET
   * line into view, not the field with its caption and picks; on iOS the
   * sheet's own `useVisualViewportSheetHeight` caps it to the visible area and
   * this then centres the field inside that.
   *
   * Same tool the sheets use, `centreInScroller`: it scrolls the nearest
   * scrollable ancestor (the sheet) and NEVER the page — see the warning there
   * about `scrollIntoView` and iOS. Already-visible → nothing moves. Re-run for
   * a second after focus on every viewport change, because the keyboard slides
   * in over ~300 ms and reports its height in steps (the a1ee022c lesson: one
   * measurement at focus time measures the viewport BEFORE the keyboard).
   */
  useEffect(() => {
    const el = inputRef.current;
    if (!el || typeof window === 'undefined') return;
    let until = 0;
    const reveal = () => {
      if (document.activeElement !== el || Date.now() > until) return;
      requestAnimationFrame(() => centreInScroller(el));
    };
    const onFocus = () => {
      until = Date.now() + 1000;
      reveal();
    };
    const vv = window.visualViewport;
    el.addEventListener('focus', onFocus);
    vv?.addEventListener('resize', reveal);
    window.addEventListener('resize', reveal);
    return () => {
      el.removeEventListener('focus', onFocus);
      vv?.removeEventListener('resize', reveal);
      window.removeEventListener('resize', reveal);
    };
  }, []);

  const compact = value ? formatBudgetCompact(value, locale) : null;
  const unit = t('tender.budget.unit');

  return (
    <div className={wrapperClassName ?? 'flex min-w-0 flex-col gap-1'}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        enterKeyHint="done"
        value={shown}
        placeholder={placeholder}
        onChange={(e) => {
          const el = e.target;
          const caret = el.selectionStart ?? el.value.length;
          let digits = budgetDigits(el.value);
          let before = el.value.slice(0, caret).replace(/\D/g, '').length;
          // Backspace over a group SPACE: the digits did not change, only the
          // separator went — so the field would just redraw it and the key
          // would seem dead. Delete the digit in front of it instead, which is
          // what the reader meant. Read off the value, not `keydown`: Android
          // keyboards report Backspace as key code 229.
          if (digits === value && el.value.length < shown.length && before > 0) {
            digits = budgetDigits(value.slice(0, before - 1) + value.slice(before));
            before -= 1;
          }
          caretDigits.current = Math.min(before, digits.length);
          onChange(digits);
        }}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={className}
        {...aria}
      />
      {/* «= 1 mlrd so'm» — the magnitude in words, so a missing or extra zero
          shows while typing. `aria-live` reads it out after a pause. */}
      {showCaption && (
        <p
          aria-live="polite"
          data-testid="budget-caption"
          className="min-h-[16px] text-[12px] leading-4 text-ink-muted"
        >
          {compact ? `= ${compact} ${unit}` : ''}
        </p>
      )}
      {presets && presets.length > 0 && (
        <div className="-mx-0.5 flex gap-1.5 overflow-x-auto px-0.5 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {presets.map((amount) => {
            const digits = String(amount);
            const active = digits === value;
            return (
              <button
                key={amount}
                type="button"
                aria-pressed={active}
                // The pick replaces the value; tapping the active one clears it.
                onClick={() => {
                  const next = active ? '' : digits;
                  onChange(next);
                  onPick?.(next);
                }}
                className={`inline-flex h-8 shrink-0 items-center rounded-pill border px-2.5 text-[12.5px] font-semibold transition-colors ${
                  active
                    ? 'border-brand/40 bg-brand-dim text-brand'
                    : 'border-edge bg-bone-surface text-ink-soft hover:border-brand/30 hover:text-ink'
                }`}
              >
                {formatBudgetCompact(digits, locale)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
